import {
    OTHER_CHARGE_DETALLE_MAX_LENGTH,
    OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH,
    StoredOtherCharge,
    generateOtherChargeId,
    isValidThirdPartyName,
    readDocumentOtherCharges,
} from "./DocumentOtherCharges";
import { OTHER_CHARGE_TYPE_THIRD_PARTY } from "./Constants";
import { toNumberFormat } from "./Utils";
import { sanitizeSafeText } from "@amplifyShared/safeText";

/**
 * Quien realiza el envio, que es lo que decide como se factura:
 * - `own`: lo hace el suscriptor. Es un servicio propio, va como linea del documento
 *   con su CABYS y por tanto genera impuesto.
 * - `external`: lo hace un courier. El suscriptor solo cobra por cuenta ajena, asi que
 *   va como OtrosCargos tipo "04" con los datos fiscales del tercero.
 */
export type ClientShippingMode = "own" | "external";

/**
 * Una forma de envio disponible para el cliente. Un cliente puede tener varias (distintos
 * couriers, o un courier y el envio propio) y al facturar se elige cual aplica.
 */
export type ClientShippingOption = {
    id: string;
    mode: ClientShippingMode;
    /** Solo aplica cuando `mode === "external"`. */
    providerId?: string;
    /** Costo de envio pactado para esta opcion. */
    amount: number;
};

/** Shape minimo del proveedor que necesita esta capa, para no atarla a la API. */
export type ShippingProviderLike = {
    id?: string | null;
    codigo?: string | null;
    nombre?: string | null;
    tipo_identificacion?: string | null;
    identificacion?: string | null;
    default_amount?: number | null;
};

/** Detalle por defecto del cargo. Es lo que el cliente ve impreso en el documento. */
export const SHIPPING_CHARGE_DETALLE = "Costo de envio";

export const CLIENT_SHIPPING_MODES: Record<string, string> = {
    own: "Envío propio",
    external: "Envío por proveedor externo",
};

/** Tope defensivo: mas opciones que esto no es una configuracion, es un error de carga. */
export const MAX_CLIENT_SHIPPING_OPTIONS = 20;

const isShippingMode = (value: unknown): value is ClientShippingMode =>
    value === "own" || value === "external";

export const generateShippingOptionId = generateOtherChargeId;

const normalizeOption = (value: unknown): ClientShippingOption | null => {
    if (!value || typeof value !== "object") return null;
    const candidate = value as Partial<ClientShippingOption>;
    if (!isShippingMode(candidate.mode)) return null;
    const providerId = `${candidate.providerId || ""}`;
    return {
        id: typeof candidate.id === "string" && candidate.id ? candidate.id : generateShippingOptionId(),
        mode: candidate.mode,
        // Una opcion propia no lleva proveedor: si quedo uno de una edicion previa se poda,
        // porque `own` no declara tercero en el documento.
        providerId: candidate.mode === "external" && providerId ? providerId : undefined,
        amount: Number(candidate.amount || 0),
    };
};

/**
 * Lee las opciones de envio del cliente. Tolera basura: un JSON corrupto o una entrada
 * invalida no debe impedir cargar la ficha del cliente.
 */
export const parseClientShippingOptions = (raw: string | null | undefined): ClientShippingOption[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map(normalizeOption)
            .filter((option): option is ClientShippingOption => option !== null)
            .slice(0, MAX_CLIENT_SHIPPING_OPTIONS);
    } catch {
        return [];
    }
};

export const serializeClientShippingOptions = (options: ClientShippingOption[]): string =>
    JSON.stringify(options.slice(0, MAX_CLIENT_SHIPPING_OPTIONS));

/** Atajo sobre un cliente ya cargado. */
export const readClientShippingOptions = (client: {
    shipping_data?: string | null;
}): ClientShippingOption[] => parseClientShippingOptions(client?.shipping_data);

/** Crea una opcion vacia lista para editar. */
export const createEmptyShippingOption = (mode: ClientShippingMode = "external"): ClientShippingOption => ({
    id: generateShippingOptionId(),
    mode,
    amount: 0,
});

/**
 * Etiqueta con la que la opcion se muestra y se elige al facturar. El proveedor identifica
 * la opcion mejor que cualquier nombre libre, asi que no se pide uno.
 */
export const describeShippingOption = (
    option: ClientShippingOption,
    providers: ShippingProviderLike[] = [],
): string => {
    if (option.mode === "own") {
        return CLIENT_SHIPPING_MODES.own;
    }
    const provider = providers.find((p) => p.id === option.providerId);
    return provider?.nombre ? `${provider.nombre}` : "Proveedor no encontrado";
};

/**
 * Un cargo "04" sin identificacion, o con un nombre de tercero que el XSD no admite, lo
 * rechaza Hacienda cuando el consecutivo ya se quemo, asi que se valida antes de dejar
 * agregarlo al documento.
 */
export const hasValidThirdPartyData = (provider: ShippingProviderLike | null | undefined): boolean =>
    !!provider
    && isValidThirdPartyName(provider.nombre)
    && !!`${provider.tipo_identificacion || ""}`.trim()
    && !!`${provider.identificacion || ""}`.trim();

/**
 * Construye el cargo de envio de un tercero. Nace en `mode: "amount"` porque un envio es
 * un monto pactado, no un porcentaje de la venta: no debe recalcularse cuando cambian las
 * lineas del documento.
 */
export const shippingChargeFromProvider = (
    provider: ShippingProviderLike,
    amount: number,
    detalle: string = SHIPPING_CHARGE_DETALLE,
): StoredOtherCharge => ({
    id: generateOtherChargeId(),
    mode: "amount",
    tipoDocumentoOC: OTHER_CHARGE_TYPE_THIRD_PARTY,
    detalle: sanitizeSafeText(detalle || SHIPPING_CHARGE_DETALLE, OTHER_CHARGE_DETALLE_MAX_LENGTH),
    nombreTercero: sanitizeSafeText(provider.nombre, OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH),
    identificacionTercero: {
        tipo: `${provider.tipo_identificacion || ""}`,
        numero: `${provider.identificacion || ""}`,
    },
    montoCargo: Number(toNumberFormat(Number(amount || 0))),
    // Marca el origen del cargo para que la impresion pueda rotularlo como envio aunque
    // el usuario haya cambiado el detalle. No viaja a Hacienda.
    source: "shipping",
});

/** Etiqueta generica de la fila de otros cargos en la impresion. */
export const OTHER_CHARGES_PRINT_LABEL = "Otros Cargos";

/** Etiqueta de la fila cuando todos los cargos del documento son el costo de envio. */
export const SHIPPING_CHARGES_PRINT_LABEL = "Costo de Envío";

const normalizeDetalle = (detalle: string | undefined): string =>
    `${detalle || ""}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();

/**
 * Un cargo tipo "04" no es necesariamente un envio: el tipo esta disponible en la captura
 * manual de otros cargos. Lo distingue la marca `source`, y para los documentos emitidos
 * antes de que existiera esa marca, el detalle por defecto del flujo de envio.
 */
export const isShippingCharge = (charge: StoredOtherCharge): boolean =>
    charge.tipoDocumentoOC === OTHER_CHARGE_TYPE_THIRD_PARTY
    && (charge.source === "shipping"
        || normalizeDetalle(charge.detalle) === normalizeDetalle(SHIPPING_CHARGE_DETALLE));

/**
 * Rotulo de la fila de otros cargos. Solo se renombra cuando el documento no cobra nada
 * mas por esa via: si el envio convive con un impuesto de servicio o un timbre, la suma
 * impresa ya no es el envio y la etiqueta generica es la unica correcta.
 */
export const resolveOtherChargesLabel = (charges: StoredOtherCharge[]): string =>
    charges.length > 0 && charges.every(isShippingCharge)
        ? SHIPPING_CHARGES_PRINT_LABEL
        : OTHER_CHARGES_PRINT_LABEL;

/** Rotulo para un documento ya persistido. Hermana de `readDocumentOtherChargesTotal`. */
export const readDocumentOtherChargesLabel = (document: {
    otros_cargos?: string | null;
    impuestos_servicio?: number | null;
}): string => resolveOtherChargesLabel(readDocumentOtherCharges(document));
